import classNames from 'classnames/bind';
import React, { useEffect, useRef, useState } from 'react';

import If from '../conditional/If/If';
import styles from './PreviewCarousel.module.scss';

const cx = classNames.bind(styles);

interface IPreviewCarouselProps {
    children: React.ReactNode;
    className?: string;
    renderNextArrow?: (next: () => void) => React.ReactNode;
    transitionDuration?: number;
}

const PreviewCarousel = ({
  children,
  className = '',
  renderNextArrow,
  transitionDuration = 2000
}: IPreviewCarouselProps) => {
  const rootRef = useRef<any>(null);
  const trackRef = useRef<any>(null);
  const [leftOffset, setLeftOffset] = useState(0);
  const [slideWidth, setSlideWidth] = useState(240);
  const [totalSlidesWidth, setTotalSlidesWidth] = useState(2880);

  const calculateSlideWidths = () => {
    const calculations = {
      totalSlidesWidthCalc: 0,
      slideWidthCalc: 0,
    };
    const slideElements = trackRef.current.children;

    for (let i = 0; i < slideElements.length; i += 1) {
      // This expects spacing between slides to be padding, not margins!
      calculations.slideWidthCalc = slideElements[i].offsetWidth;
      calculations.totalSlidesWidthCalc += calculations.slideWidthCalc;
    }

    return calculations;
  };

  /*
    Here we calculate the width of the ticker viewport to determine how many slides are visible.
    Then we clone the visible slides so when we wrap around to the start again it appears that we
    looped around to the same slides we started with. After we reset the left value we start again.
  */
  const appendDummySlides = (slideWidthCalc: number) => {
    const visibleSlides = Math.ceil(rootRef.current.offsetWidth / slideWidthCalc);

    for (let i = 0; i < trackRef.current.childNodes.length; i += 1) {
      if (i <= visibleSlides) {
        const slide = trackRef.current.childNodes[i];
        const slideClone = slide.cloneNode(true);
        trackRef.current.appendChild(slideClone);
      }
    }
  };

  useEffect(() => {
    const { slideWidthCalc, totalSlidesWidthCalc } = calculateSlideWidths();
    appendDummySlides(slideWidthCalc);
    setSlideWidth(slideWidthCalc);
    setTotalSlidesWidth(totalSlidesWidthCalc);
  }, []);

  const handleSlideChange = () => {
    let newPosition = leftOffset + slideWidth;
    if (newPosition > totalSlidesWidth) {
      newPosition = 0;
    }
    setLeftOffset(newPosition);
  };

  const getTrackStyles = () => {
    return {
      transform: `translateX(-${leftOffset}px)`,
      transitionDuration: `${leftOffset === 0 ? 0 : transitionDuration}ms`,
    };
  };

  const getRootClasses = () => {
    return cx({
      [styles.Root]: true,
      [className]: className !== undefined,
    });
  };

  return (
    <>
      <div
        className={ getRootClasses() }
        ref={ rootRef }
      >
        <div
          className={ styles.Track }
          ref={ trackRef }
          style={ getTrackStyles() }
        >
          { children }
        </div>
      </div>

      <If condition={ renderNextArrow !== undefined }>
        { renderNextArrow ? renderNextArrow(handleSlideChange) : '' }
      </If>
    </>
  );
};

export default PreviewCarousel;
